import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client.js';

const format = (n) => (n ?? 0).toLocaleString('de-DE');

/**
 * Aktivitätsstatistik erfassen und Zuwächse anzeigen.
 *
 * Ein einzelner Stand sagt wenig. Erst zwei Stände — vor und nach einem
 * Community Day — beantworten die Frage, um die es geht: wie viel dabei
 * zusammengekommen ist.
 */
export default function StatsPage() {
  const [verlauf, setVerlauf] = useState(null);
  const [vorraete, setVorraete] = useState([]);
  const [fehler, setFehler] = useState(null);
  const [busy, setBusy] = useState(false);
  const feld = useRef(null);

  const laden = () =>
    api.stats.verlauf().then((d) => setVerlauf(d.verlauf)).catch((e) => setFehler(e.message));

  useEffect(() => {
    laden();
    api.vorraete().then((d) => setVorraete(d.arten)).catch(() => setVorraete([]));
  }, []);

  const erfassen = async (datei) => {
    if (!datei) return;
    setBusy(true);
    setFehler(null);
    try {
      const d = await api.stats.erfassen(datei);
      if (!d.ok) {
        setFehler(d.fehler);
      } else {
        await laden();
      }
    } catch (e) {
      setFehler(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="hero">
        <span className="hero__tag">Statistik</span>
        <h1>Was hast du gefangen?</h1>
        <p className="hero__sub">
          Screenshot deines Profils vor und nach einem Event — der Zuwachs
          ergibt sich aus dem Vergleich.
        </p>
      </section>

      <div className="inline" style={{ marginBottom: 24 }}>
        <input
          ref={feld}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => erfassen(e.target.files?.[0])}
        />
        <button className="btn btn--primary" onClick={() => feld.current?.click()} disabled={busy}>
          {busy ? 'Liest…' : 'Stand erfassen'}
        </button>
      </div>

      {fehler && <div className="error">{fehler}</div>}

      {vorraete.length > 0 && (
        <section style={{ marginBottom: 32 }}>
          <h2 className="abschnitt">Bonbons und Mega-Energie</h2>
          <p className="card__meta" style={{ marginBottom: 12 }}>
            Erfasst beim Scannen ohne geöffnete Bewertung.
          </p>

          <div className="stack">
            {vorraete.map((v) => (
              <article className="stat-karte" key={v.art}>
                <div className="spread">
                  <strong>{v.art}</strong>
                  <span className="text-faint">
                    {new Date(v.erfasstAm).toLocaleDateString('de-DE')}
                  </span>
                </div>

                <div className="stat-karte__werte">
                  {v.bonbon !== null && <span>{format(v.bonbon)} Bonbon</span>}
                  {v.bonbonXl !== null && <span>{format(v.bonbonXl)} XL</span>}
                  {v.megaEnergie !== null && <span>{format(v.megaEnergie)} Mega-Energie</span>}
                </div>

                {(v.entwicklungReicht !== null || v.megaReicht !== null) && (
                  <div className="stat-karte__zuwachs">
                    {v.entwicklungReicht !== null && (
                      v.entwicklungReicht > 0
                        ? <>Entwicklung möglich ({v.entwicklungKosten} nötig
                            {v.entwicklungReicht > 1 && <>, reicht {v.entwicklungReicht}-mal</>})</>
                        : <>Noch {format(v.entwicklungKosten - (v.bonbon ?? 0))} Bonbon bis zur Entwicklung</>
                    )}
                    {v.megaReicht !== null && (
                      <>
                        {v.entwicklungReicht !== null && ' · '}
                        {v.megaReicht
                          ? <>Mega-Entwicklung möglich</>
                          : <>Noch {format(v.megaKosten - (v.megaEnergie ?? 0))} Mega-Energie</>}
                      </>
                    )}
                  </div>
                )}

                {v.zuwachs && (v.zuwachs.bonbon || v.zuwachs.megaEnergie) && (
                  <div className="card__meta">
                    seit {new Date(v.zuwachs.seit).toLocaleDateString('de-DE')}:
                    {v.zuwachs.bonbon !== null && <> +{format(v.zuwachs.bonbon)} Bonbon</>}
                    {v.zuwachs.megaEnergie ? <> · +{format(v.zuwachs.megaEnergie)} Energie</> : null}
                  </div>
                )}
              </article>
            ))}
          </div>
        </section>
      )}

      {!verlauf ? (
        <div className="skeleton" style={{ height: 200 }} />
      ) : verlauf.length === 0 ? (
        <div className="empty">
          <p>Noch kein Stand erfasst.</p>
          <p className="text-faint">
            Im Spiel auf dein Profil gehen, bis die Aktivitätsstatistik
            sichtbar ist, und einen Screenshot machen.
          </p>
        </div>
      ) : (
        <div className="stack">
          {verlauf.map((s) => (
            <article className="stat-karte" key={s.id}>
              <div className="spread">
                <strong>
                  {new Date(s.erfasstAm).toLocaleString('de-DE', {
                    day: '2-digit', month: '2-digit', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                  })}
                </strong>
                {s.level !== null && <span className="badge badge--soft">Level {s.level}</span>}
              </div>

              <div className="stat-karte__werte">
                <span>{format(s.gefangen)} gefangen</span>
                <span>{format(s.pokestops)} Stops</span>
                <span>{format(s.ep)} EP</span>
                <span>{format(Math.round((s.distanzM ?? 0) / 1000))} km</span>
              </div>

              {s.zuwachs && (
                <div className="stat-karte__zuwachs">
                  <span className="text-faint">
                    seit {new Date(s.zuwachs.seit).toLocaleString('de-DE', {
                      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                    })}:
                  </span>
                  {' '}
                  <strong>+{format(s.zuwachs.gefangen)}</strong> gefangen ·{' '}
                  <strong>+{format(s.zuwachs.pokestops)}</strong> Stops ·{' '}
                  <strong>+{format(s.zuwachs.ep)}</strong> EP ·{' '}
                  <strong>+{((s.zuwachs.distanzM ?? 0) / 1000).toFixed(1)}</strong> km
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </>
  );
}
